// @givmo/app-admin — the employer auth-policy write, addressed by URL (AS-WEB4).
//
// ══ WHY THIS FILE EXISTS ══════════════════════════════════════════════════════════════════════
// AS-BE10 landed `PUT /admin/company/settings/auth-policy` AFTER the committed OpenAPI snapshot was
// last refreshed, so there is no generated SDK operation and no generated request type.
// `agent_docs/api-client.md` § *A whole ROUTE the snapshot lacks* is the sanctioned disposition —
// and explicitly NOT "refresh the snapshot for one feature", which would absorb every unrelated
// backend change since the last regen and collide with any regen in flight.
//
// `givmoClient` is the same `@hey-api/client-fetch` client the generated operations bind to, so this
// request inherits the whole interceptor stack — Bearer, App Check, `X-Request-Id` (`wb-`), the
// `{data,meta,links}` unwrap, and the `{errors:[…]}` → `GivmoError` mapping. Nothing differs from a
// generated call except who wrote the URL.
//
// ══ WHAT THE FAILURES MEAN ════════════════════════════════════════════════════════════════════
// ⚠ 404 = the SURFACE IS OFF, not "no policy". `_require_employer_auth_policy_enabled` is a route
// DEPENDENCY, so while `EMPLOYER_AUTH_POLICY_ENABLED` is dark every shape of request answers 404 —
// deliberately, so a prober cannot tell the route exists or learn its schema. The tab treats it as
// "not available in this environment", never as an error an Owner could act on.
//
// ⚠ 403 = a Member admin. The write is Owner-only server-side (`company:edit` on the service
// method); the tab's own Owner gate is a UI convenience and this is the real control.
//
// ⚠ The GET is the DARK GATE, and it is a different route from the ungated policy read. The two
// fields also ride `GET /admin/company` (additive on `CompanyDetailedOut`) and are served regardless
// of the flag — so that read can never answer "is this feature on". This route can, because it sits
// behind the same `_require_employer_auth_policy_enabled` dependency as the PUT: 200 means the
// surface is live, anything else means it is not. That is what lets the tab reveal on the SERVER
// answering, with no client flag to fall out of step with it.

import { givmoClient } from '@givmo/api-client';

/** The one route this feature writes to. Owner-gated (`company:edit`) server-side. */
export const AUTH_POLICY_URL = '/admin/company/settings/auth-policy';

/**
 * `PUT /admin/company/settings/auth-policy` body.
 *
 * A WHOLE-STATE restatement — both fields required. `CompanyUpdateAuthPolicy` models it that way
 * (the consent-screen convention, not the omit-means-unchanged one) because an Owner submitting the
 * Security screen is stating the whole policy, and a partial update would let a stale client keep a
 * tier the Owner believed they had changed.
 *
 * ★ The body is `extra="forbid"` server-side, which is load-bearing rather than tidy: the ladder
 * continues into SSO (L3/L4) whose column does not exist until P4, and without `forbid` a client
 * sending `{"sso_mode": …}` would get a 200 and believe it had locked the company down. So this type
 * names exactly the two keys the backend can honor and `MfaPolicyTier` is a closed union — an
 * unenforceable tier is unspellable here, not merely refused.
 */
export interface AuthPolicyWriteBody {
  mfa_policy: 'none' | 'encourage' | 'require';
  mfa_grace_days: number;
}

/** What the probe learned: whether the surface is live, and the policy it served if so. */
export interface AuthPolicyProbeResult {
  available: boolean;
  /** The response body on a 200 — the stored policy, same shape the PUT echoes. `null` otherwise. */
  body: unknown;
}

/**
 * Ask whether the employer auth-policy surface exists in this environment.
 *
 * ⚠ KEYED ON THE ANSWER ARRIVING, NOT ON A STATUS CODE, and deliberately so. Only a 200 reveals;
 * every other outcome hides. That covers more than the dark 404 — before the backend GET ships, this
 * path is registered for PUT only and a GET answers **405** (a method mismatch resolves before route
 * dependencies), which must read as "not available" exactly like the 404 does. A 403, a 500 and a
 * transport failure all resolve the same way, because a tab this client cannot confirm is live is a
 * tab it must not show.
 *
 * ⚠ SILENT. No toast, no console line, no telemetry. While the surface is dark the failure is the
 * EXPECTED answer, and logging it would put a line in every Owner's console on every visit to
 * `/settings`, in production. Same rail as the passkey probe (`agent_docs/auth.md`).
 *
 * The body is returned rather than decoded here: the response type is not in the snapshot either, so
 * the tolerant read belongs beside the feature.
 */
export async function probeAuthPolicy(): Promise<AuthPolicyProbeResult> {
  try {
    const { data } = await givmoClient.get({ url: AUTH_POLICY_URL, throwOnError: true });
    return { available: true, body: data };
  } catch {
    return { available: false, body: null };
  }
}

/**
 * Store the company's employee authentication policy.
 *
 * Returns the saved `CompanyDetailedOut` as `unknown` — the response type predates the two fields in
 * the snapshot, so it goes through `readAuthPolicy` exactly like the read does. `throwOnError` so a
 * failure surfaces as the shared `GivmoError` the rest of the app already renders, rather than a
 * second error convention here.
 */
export async function saveAuthPolicy(body: AuthPolicyWriteBody): Promise<unknown> {
  const { data } = await givmoClient.put({ url: AUTH_POLICY_URL, body, throwOnError: true });
  return data;
}
